import React, { useMemo, useRef } from 'react';
import { X } from 'lucide-react';
import OsScroll from './ui/OsScroll';
import useDbTypeColors from './dbManager/useDbTypeColors';
import { useDbColumnResize, ColumnResizeHandle } from './useDbColumnResize';
import { cellToText } from './dbCellText';
import {
  EMPTY_COLUMN_WIDTHS,
  EMPTY_FIND_KEYS,
  EMPTY_PENDING_DELETES,
  EMPTY_PENDING_ROWS,
  cellKey,
  parseCellKey,
} from './dbGridModel';
import {
  renderCellValue,
  shortColumnType,
  dateColumnKind,
  DateEditButton,
  FkNavigateButton,
} from './dbGridCells';
import DbEnumCellEditor from './dbManager/DbEnumCellEditor';
import DbEnumSelect from './dbManager/DbEnumSelect';

// DBeaver-style transposed/record view: columns become rows and each
// selected row becomes its own column, so a wide table can be read
// top-to-bottom instead of scrolled horizontally. Cells edit exactly like
// the normal grid — same editingCell/pendingEdits keyed by rowIndex:colIndex,
// so an edit started here or in the horizontal view survives flipping
// between them.
export default function TransposedGrid({
  result,
  selectedRows,
  pendingEdits,
  // Rows marked for deletion. Here a source ROW is a COLUMN, so the mark lands
  // on that column's header and cells rather than on a `<tr>`.
  pendingDeletes = EMPTY_PENDING_DELETES,
  // Defaulted, because the tab does not pass it here — it only ever renders
  // the horizontal grid with an offset. Without the default this was
  // `undefined + i + 1`, and every column header in the record view read
  // "Row NaN".
  rowNumberOffset = 0,
  columnTypes,
  // ENUM columns: name → { values, nullable } — see DbResultsGrid.
  enumByColumn,
  editable,
  editingCell,
  editingValue,
  onCellOpen,
  onCellContextMenu,
  onEditingValueChange,
  onCommitEdit,
  onCancelEdit,
  onEditNull,
  // Tab inside the cell editor — see DbResultsGrid's identical prop.
  onEditTab,
  pendingRows = EMPTY_PENDING_ROWS,
  onUpdatePendingCell,
  onRemovePendingRow,
  selectedCells,
  onCellSelect,
  onDatePick,
  fkByColumn,
  onFkNavigate,
  transposedWidths = EMPTY_COLUMN_WIDTHS,
  onTransposedResize,
  // True when this view was just flipped into, i.e. "put me where I was" is
  // still an open question. See the anchor effect below.
  anchorToSelection = false,
  // Ctrl+F. Same cellKey space as the horizontal grid, so a match found in one
  // view is the same match in the other — see the search's `rowIndexes`, which
  // is what keeps the count describing only cells this view can actually draw.
  findMatchKeys = EMPTY_FIND_KEYS,
  activeFindKey = null,
  cursorKey = null,
}) {
  const rowIndexes = useMemo(() => Array.from(selectedRows).sort((a, b) => a - b), [selectedRows]);
  // Colour values by column type — same switch and colours as the grid.
  const typeColors = useDbTypeColors(result?.columns, columnTypes);

  // Keep the keyboard's cell on screen (dbManager/useDbGridKeyboardNav.js).
  React.useEffect(() => {
    if (!cursorKey) return undefined;
    const frame = requestAnimationFrame(() => {
      document.querySelector(`[data-cell-key="${cursorKey}"]`)?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    });
    return () => cancelAnimationFrame(frame);
  }, [cursorKey]);
  // Keyed by POSITION, not by row: in this view a column is "the second record
  // on screen", and which record that is changes with the selection. A width
  // pinned to a row index would follow rows in and out of view, which is not
  // what dragging a column boundary means here.
  const hasTransposedWidths = Object.keys(transposedWidths).length > 0;
  const { beginResize, guideNode } = useDbColumnResize({
    onCommit: onTransposedResize || (() => {}),
    onSeed: (table) => {
      if (hasTransposedWidths) return;
      const widths = {};
      table.querySelectorAll('thead th').forEach((cell, i) => {
        widths[i === 0 ? 'label' : `pos${i - 1}`] = cell.offsetWidth;
      });
      onTransposedResize(null, widths);
    },
  });
  // Same gesture rules as the horizontal grid — see ResultsGrid.cellMouseDown.
  const cellMouseDown = (e, kind, id, colIndex) => {
    if (!onCellSelect) return;
    if (e.ctrlKey || e.metaKey || e.shiftKey) e.preventDefault();
    onCellSelect(e, kind, id, colIndex);
  };
  /**
   * Width for one column, with a fallback for positions that were never sized.
   *
   * `onSeed` only runs when a boundary is DRAGGED, and it snapshots the
   * columns that exist at that moment, keyed by position. Select more rows
   * afterwards and the new positions have no entry — and under
   * `table-layout: fixed` an unsized `<col>` does not size to its content, it
   * takes a share of whatever is left over. With the seeded columns already
   * filling the table that share is a few pixels, which is how one wide record
   * column ended up next to a row of slivers.
   *
   * An unsized RECORD column is sized from the first one — but at HALF its
   * width, floored so it stays readable.
   *
   * Not the full width, because `pos0` is usually an outlier: it gets seeded
   * while it is the only record column on screen, so auto layout has already
   * handed it everything the table had. Copying that to each new column made
   * them all enormous. Half of it is roughly what the columns settle to once
   * there are several, and a manual resize still shows through because it is
   * what the halving is measured from.
   */
  const RECORD_COL_MIN = 140;
  const recordColFallback = Math.max(
    RECORD_COL_MIN,
    Math.round((transposedWidths.pos0 || 220) / 2)
  );
  const colWidth = (key) => {
    const stored = transposedWidths[key];
    if (stored) return { width: `${stored}px` };
    if (key === 'label') return undefined;
    return { width: `${recordColFallback}px` };
  };

  /**
   * The cell that was selected on the way in, so it can be FOCUSED on arrival.
   *
   * Focused, not scrolled to. This view had no restore of any kind, which is
   * the whole of the "editing is smooth, selecting is not" difference: an open
   * editor renders `<input autoFocus>`, and focusing an element scrolls it into
   * view for free. A merely-selected cell had no focused element, so nothing
   * moved and the flip landed at the top of a table whose rows are the columns
   * you were nowhere near.
   *
   * Copying the mechanism rather than the effect matters here. `OsScroll` passes
   * `defer`, so OverlayScrollbars reparents this table into a fresh viewport
   * after paint — any scroll set before that is discarded. Focus survives the
   * reparent, which is why the edit path always worked and why a hand-rolled
   * `scrollIntoView` in its place did not.
   *
   * Once per mount only: this view is entered by flipping, and a later click
   * inside it must not be yanked around by its own selection.
   */
  const anchorCell = useMemo(() => {
    if (!anchorToSelection) return null;
    // Same precedence as the horizontal grid: the cell being edited, then a
    // selected cell. A row selection alone has nothing to aim at here — every
    // record column is already a column of this table.
    if (editingCell && Number.isInteger(editingCell.rowIndex) && Number.isInteger(editingCell.colIndex)) {
      return { row: editingCell.rowIndex, col: editingCell.colIndex };
    }
    if (selectedCells?.size) {
      for (const key of selectedCells) {
        const { kind, id, col } = parseCellKey(key);
        if (kind === 'r' && Number.isInteger(id) && Number.isInteger(col)) return { row: id, col };
      }
    }
    return null;
    // Deliberately not re-run when the selection moves — see above. The memo
    // is for the mount-time read only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // Focus the anchor cell exactly the way `autoFocus` focuses the editor: from
  // a ref that runs during commit. Guarded so it happens once per mount — the
  // anchor is "where you came in", not "whatever is selected now".
  const focusedAnchor = useRef(false);
  const focusAnchorCell = (el) => {
    if (!el || focusedAnchor.current) return;
    focusedAnchor.current = true;
    el.focus();
  };

  return (
    <>
    {guideNode}
    <OsScroll className={`dbm-grid-wrap${typeColors.rootClass}`} style={typeColors.style}>
      <table className={`dbm-table dbm-table--transposed${hasTransposedWidths ? ' dbm-table--fixed' : ''}`}>
        {hasTransposedWidths && (
          <colgroup>
            <col style={colWidth('label')} />
            {rowIndexes.map((i, pos) => (
              <col key={i} style={colWidth(`pos${pos}`)} />
            ))}
            {pendingRows.map((pRow, idx) => (
              <col key={pRow.id} style={colWidth(`pos${rowIndexes.length + idx}`)} />
            ))}
          </colgroup>
        )}
        <thead>
          <tr>
            <th className="dbm-th dbm-th--colname">
              Column
              {onTransposedResize && (
                <ColumnResizeHandle
                  onBegin={(e) => beginResize(e, 'label')}
                  onReset={() => onTransposedResize('label', null)}
                />
              )}
            </th>
            {rowIndexes.map((i, pos) => (
              <th
                key={i}
                className={pendingDeletes.has(i) ? 'dbm-th dbm-th--deleting' : 'dbm-th'}
              >
                Row {rowNumberOffset + i + 1}
                {onTransposedResize && (
                  <ColumnResizeHandle
                    onBegin={(e) => beginResize(e, `pos${pos}`)}
                    onReset={() => onTransposedResize(`pos${pos}`, null)}
                  />
                )}
              </th>
            ))}
            {pendingRows.map((pRow) => (
              <th key={pRow.id} className="dbm-th dbm-th--pending">
                New
                {onRemovePendingRow && (
                  <button
                    type="button"
                    className="dbm-remove-pending-btn"
                    title="Discard row"
                    onClick={() => onRemovePendingRow(pRow.id)}
                  >
                    <X size={12} />
                  </button>
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {result.columns.map((col, j) => {
            const shortType = columnTypes ? shortColumnType(columnTypes[col]) : null;
            const dtype = onDatePick ? dateColumnKind(columnTypes?.[col]) : null;
            return (
              <tr key={col}>
                <td className="dbm-td dbm-td-colname" title={col}>
                  {col}
                  {shortType && <span className="dbm-th-type">{shortType}</span>}
                </td>
                {rowIndexes.map((i) => {
                  const cell = result.rows[i][j];
                  const isEditing = editable && editingCell && editingCell.rowIndex === i && editingCell.colIndex === j;
                  const editKey = `${i}:${j}`;
                  const isEdited = Object.prototype.hasOwnProperty.call(pendingEdits, editKey);
                  const displayValue = isEdited ? pendingEdits[editKey] : cell;
                  const fkHere = onFkNavigate ? fkByColumn?.get(col) : null;
                  const findKey = cellKey('r', i, j);
                  const findClass = findMatchKeys.has(findKey)
                    ? findKey === activeFindKey
                      ? ' dbm-td--find-match dbm-td--find-active'
                      : ' dbm-td--find-match'
                    : '';
                  return (
                    <td
                      key={i}
                      // Absolute indexes into `result`, so they mean the same
                      // thing as the ones the horizontal grid writes.
                      data-row-index={i}
                      data-col-index={j}
                      data-cell-key={findKey}
                      // Focusable only so the flip has somewhere to land. -1
                      // keeps it out of the tab order: Tab is the view toggle
                      // here, and a grid whose every cell was a tab stop would
                      // take it away.
                      tabIndex={anchorCell && anchorCell.row === i && anchorCell.col === j ? -1 : undefined}
                      ref={anchorCell && anchorCell.row === i && anchorCell.col === j && !isEditing ? focusAnchorCell : undefined}
                      className={`dbm-td${isEdited && !isEditing ? ' dbm-td-edited' : ''}${
                        selectedCells?.has(cellKey('r', i, j)) ? ' dbm-td--cell-selected' : ''
                      }${dtype ? ' dbm-td--date' : ''}${fkHere ? ' dbm-td--fk' : ''}${
                        pendingDeletes.has(i) ? ' dbm-td--deleting' : ''
                      }${findClass}${typeColors.catClass(j, displayValue)}`}
                      onMouseDown={(e) => cellMouseDown(e, 'r', i, j)}
                      onDoubleClick={onCellOpen ? () => onCellOpen(i, j, cell) : undefined}
                      onContextMenu={onCellContextMenu ? (e) => onCellContextMenu(e, i, j) : undefined}
                    >
                      {isEditing && enumByColumn?.get(col) ? (
                        <DbEnumCellEditor
                          values={enumByColumn.get(col).values}
                          nullable={enumByColumn.get(col).nullable}
                          value={displayValue}
                          editingValue={editingValue}
                          onEditingValueChange={onEditingValueChange}
                          onCommitEdit={onCommitEdit}
                          onCancelEdit={onCancelEdit}
                          onEditNull={onEditNull}
                          onEditTab={onEditTab}
                        />
                      ) : isEditing ? (
                        <div className="dbm-edit-wrap">
                          <span className="dbm-edit-ghost" aria-hidden>
                            {displayValue === null ? 'NULL' : cellToText(displayValue)}
                          </span>
                          <input
                            type="text"
                            autoFocus
                            className="dbm-pending-input dbm-edit-input"
                            // Caret at the end: type-to-edit starts with the typed character, and
                            // F2 / double-click continue the value (Excel / DBeaver).
                            onFocus={(e) => e.target.setSelectionRange(e.target.value.length, e.target.value.length)}
                            value={editingValue}
                            placeholder=""
                            onChange={(e) => onEditingValueChange(e.target.value)}
                            onBlur={onCommitEdit}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                onCommitEdit();
                              } else if (e.key === 'Escape') {
                                e.preventDefault();
                                onCancelEdit();
                              } else if (e.key === 'Tab' && onEditTab) {
                                // The way back out of the record view without
                                // losing the caret — see the same branch in
                                // DbResultsGrid.
                                e.preventDefault();
                                onEditTab();
                              } else if (e.altKey && e.code === 'KeyN' && onEditNull) {
                                e.preventDefault();
                                onEditNull();
                              }
                            }}
                          />
                        </div>
                      ) : (
                        <>
                          {renderCellValue(displayValue)}
                          {dtype && (
                            <DateEditButton kind={dtype} onOpen={(rect) => onDatePick('saved', i, j, rect)} />
                          )}
                          {fkHere && displayValue !== null && (
                            <FkNavigateButton
                              title={`Go to ${fkHere.fk.refTable} (${fkHere.fk.name})`}
                              onNavigate={() => onFkNavigate(i, j)}
                            />
                          )}
                        </>
                      )}
                    </td>
                  );
                })}
                {pendingRows.map((pRow) => (
                  <td
                    key={pRow.id}
                    className={`dbm-td${selectedCells?.has(cellKey('p', pRow.id, j)) ? ' dbm-td--cell-selected' : ''}${dtype ? ' dbm-td--date' : ''}`}
                    data-cell-key={cellKey('p', pRow.id, j)}
                    onMouseDown={(e) => cellMouseDown(e, 'p', pRow.id, j)}
                  >
                    {enumByColumn?.get(col) ? (
                      <DbEnumSelect
                        meta={enumByColumn.get(col)}
                        columnName={col}
                        blankIsNull
                        value={pRow.values[j] === '' ? null : pRow.values[j]}
                        onChange={(v) => onUpdatePendingCell && onUpdatePendingCell(pRow.id, j, v ?? '')}
                      />
                    ) : (
                    <input
                      type="text"
                      size={1}
                      className="dbm-pending-input"
                      value={pRow.values[j]}
                      placeholder="NULL"
                      onChange={(e) => onUpdatePendingCell && onUpdatePendingCell(pRow.id, j, e.target.value)}
                    />
                    )}
                    {dtype && (
                      <DateEditButton kind={dtype} onOpen={(rect) => onDatePick('pending', pRow.id, j, rect)} />
                    )}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </OsScroll>
    </>
  );
}

/**
 * Host-rendered results grid for a DB Manager tab. Runs its own direct
 * `db_mysql_query` invoke (same Tauri command the db:mysql capability bridge
 * uses) rather than round-tripping through the guest iframe, since the query
 * result only needs to reach this tab's own React state, not the guest.
 *
 * A tab bound to a table (opened by clicking a table node) shows a
 * DBeaver-style filter bar: the text is a raw SQL WHERE-clause fragment
 * appended verbatim after `WHERE`, not parsed/parameterized — this mirrors
 * the DB Manager extension's own pre-existing free-form-SQL trust boundary.
 * A blank tab (opened via the tab strip's + button while DB Manager is
 * active) instead shows a full SQL editor with no table bound to it.
 */
